import { FC } from 'react';
import Grid from '@mui/material/Grid';
import usImg from '../../media/aboutus.png';
import { Typography, Button, Box, useMediaQuery } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import useTranslations from '../global/translation/hooks/translation';
import { useTheme } from '@mui/material/styles';
import { useThemeContext } from '../global/ThemeContext';
import { designTokens } from '../../theme/designTokens';

const About: FC = () => {
  const { currentLanguage, translations } = useTranslations();
  const text = translations.label.aboutUsTextShort[currentLanguage];
  const theme = useTheme();
  const { darkMode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const palette = darkMode ? designTokens.colors.dark : designTokens.colors.light;

  return (
    <Grid
      my={6}
      py={isMobile ? 6 : 10}
      container
      justifyContent="center"
      alignItems="flex-start"
      sx={{
        bgcolor: darkMode ? palette.background.default : '#FFFFFF',
        maxWidth: '1200px',
        mx: 'auto',
        px: isMobile ? 2 : 4,
        borderRadius: { xs: 0, md: '16px' },
        overflow: 'hidden',
      }}
    >
      {/* Title */}
      <Grid item xs={12} mb={4}>
        <Typography
          variant="h4"
          fontWeight={300}
          sx={{
            color: darkMode ? '#FFFFFF' : palette.primary,
            textAlign: 'center',
            letterSpacing: '-0.5px',
          }}
        >
          {translations.menuItems.aboutUs[currentLanguage]}
        </Typography>
      </Grid>

      {/* Image + Button Column */}
      <Grid item xs={12} md={5}>
        <Box
          sx={{
            mb: 3,
            position: 'relative',
          }}
        >
          {/* Decorative accent box */}
          <Box
            sx={{
              position: 'absolute',
              top: '20px',
              right: '-20px',
              width: '80%',
              height: '80%',
              border: `2px solid ${palette.accent}`,
              borderRadius: '50%',
              zIndex: 0,
            }}
          />

          {/* Image container */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              borderRadius: '50%',
              overflow: 'hidden',
              boxShadow: darkMode ? '0 10px 24px rgba(0, 0, 0, 0.32)' : '0 18px 38px rgba(20, 56, 125, 0.12)',
            }}
          >
            <div style={{ position: 'relative', paddingTop: '100%', margin: '0' }}>
              <img
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                src={usImg}
                alt="Barbara Urban-Jimenez"
              />
            </div>
          </Box>

          {/* Small accent line */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '-10px',
              left: '20px',
              width: '60px',
              height: '3px',
              bgcolor: theme.palette.primary.main,
              borderRadius: '2px',
              zIndex: 2,
            }}
          />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            href="https://dziendobry.tvn.pl/styl-zycia/zwiazki-i-seks/barbara-urban-jimenzes-dzieki-pasji-odnalazla-prawdziwe-uczucie-st5741886"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<PlayArrow />}
            sx={{
              borderColor: darkMode ? palette.accent : palette.primary,
              color: darkMode ? '#FFFFFF' : palette.primary,
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              px: 3,
              py: 1.5,
              borderRadius: '10px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: palette.accent,
                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(20, 56, 125,0.04)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            {translations.buttons.watchTvnInterview[currentLanguage]}
          </Button>
        </Box>
      </Grid>

      {/* Text Column */}
      <Grid item xs={12} md={7} sx={{ pl: isMobile ? 0 : 3 }}>
        <Typography
          variant="body1"
          sx={{
            color: darkMode ? '#D7E2F5' : '#536078',
            lineHeight: 1.8,
            fontSize: '16px',
          }}
        >
          {text}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default About;
